/**
 * Playback follows the chart's song (W2-10 projects-data; DESIGN.md §4.18, §5.13).
 *
 * Replacing the song loads the new file into the audio engine at once (`playSong` with the bytes at hand). Undo,
 * redo and a version restore change `meta.music` back and forth too, so `followSong` (the Assets module's `init`)
 * watches committed changes of the open chart and, when the song differs from the one the engine holds, loads it
 * from storage. Opening a chart is not a change here: the project session loads the song of an opened chart.
 *
 * Loads are serialised per audio engine and only the newest wish counts: undoing and redoing quickly never leaves
 * the engine on the song that was asked for first.
 */

import type { AppServices } from '../../app/registry/types';
import type { AssetId } from '../../core/model/types';
import type { ChangeSet } from '../../core/ops/types';
import type { Unsubscribe } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import { m } from './assets.messages';

type Deps = Pick<AppServices, 'audio' | 'persist' | 'ui'>;

interface SyncState {
  /** The song playback should hold (newest wish). */
  wanted: AssetId | null;
  /** Bytes already at hand for a wished song (a replacement). */
  readonly bytes: Map<AssetId, Uint8Array>;
  running: boolean;
}

const states = new WeakMap<object, SyncState>();

function stateOf(audio: object): SyncState {
  let st = states.get(audio);
  if (st === undefined) {
    st = { wanted: null, bytes: new Map(), running: false };
    states.set(audio, st);
  }
  return st;
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(err);
}

function arrayBufferOf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function pump(services: Deps, st: SyncState): Promise<void> {
  if (st.running) return;
  st.running = true;
  let held: AssetId | null = services.audio.music?.asset ?? null;
  try {
    while (st.wanted !== null && st.wanted !== held) {
      const asset = st.wanted;
      try {
        const bytes = st.bytes.get(asset) ?? (await services.persist.getAsset(asset));
        st.bytes.delete(asset);
        // Another song was asked for while this one was read: load that one instead.
        if (st.wanted !== asset) continue;
        await services.audio.loadMusic(asset, arrayBufferOf(bytes));
        held = asset;
      } catch (err) {
        st.bytes.delete(asset);
        if (st.wanted !== asset) continue;
        st.wanted = null;
        services.ui.toast({ key: m.songLoadFailed, params: { reason: errorText(err) } }, { kind: 'warning' });
      }
    }
  } finally {
    st.running = false;
  }
}

/**
 * Makes playback use `asset` (its `bytes` when they are at hand, else read from storage). A failure shows as a
 * warning toast. While another load is under way this only records the wish (that load continues with it) and
 * resolves at once; otherwise it resolves when the engine holds the newest wished song or gave up.
 */
export function playSong(services: Deps, asset: AssetId, bytes?: Uint8Array): Promise<void> {
  const st = stateOf(services.audio);
  st.wanted = asset;
  if (bytes !== undefined) st.bytes.set(asset, bytes);
  return pump(services, st);
}

/** Committed changes (not live drag updates, not the open of a chart). */
function isEdit(cs: ChangeSet): boolean {
  return cs.source === 'txn' || cs.source === 'undo' || cs.source === 'redo';
}

/**
 * Loads the chart's song into playback whenever an edit, undo, redo or restore changes it. Returns the disposer
 * (the Assets module's `init`).
 */
export function followSong(
  services: Pick<AppServices, 'store' | 'onChange' | 'audio' | 'persist' | 'ui'>,
): Unsubscribe {
  let store: DocStore | null = null;
  let off: Unsubscribe | null = null;
  let seen: AssetId | null = null;
  let scheduled = false;
  let disposed = false;

  const check = (): void => {
    scheduled = false;
    if (disposed || store === null || services.store !== store) return;
    const music = store.doc.meta.music;
    if (music === seen) return;
    seen = music;
    // Also when the engine holds it already: that cancels a load of another song still under way.
    if (music !== null) void playSong(services, music);
  };

  const attach = (): void => {
    if (services.store === store) return;
    off?.();
    off = null;
    store = services.store;
    // The song of a chart being opened is the session's to load.
    seen = store?.doc.meta.music ?? null;
    if (store === null) return;
    const watched = store;
    off = watched.subscribe((cs) => {
      if (!cs.dirty.meta) return;
      if (!isEdit(cs)) {
        seen = watched.doc.meta.music;
        return;
      }
      // After the transaction's own follow-up (a replacement hands its bytes over right after committing).
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(check);
    });
  };

  attach();
  const offChange = services.onChange(attach);
  return () => {
    disposed = true;
    offChange();
    off?.();
    off = null;
  };
}
